import React, { useState, useEffect } from 'react';
import { getBotDetails, updateBotDetails } from '../../../../utils/botUtils';
import { saveSelectedAiModel, getSelectedAiModel } from '../../../../utils/embeddingUtils';
import Swal from 'sweetalert2';
import Switch from 'react-switch';
import './botDetail.scss';

export const BotDetails: React.FC = () => {
  const [botName, setBotName] = useState('');
  const [botId, setBotId] = useState('');
  const [createdDate, setCreatedDate] = useState('');
  const [updatedBotName, setUpdatedBotName] = useState('');
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const [isActive, setIsActive] = useState(true);
  const [botStatus, setBotStatus] = useState('');
  const [chatWindowVariant, setChatWindowVariant] = useState('');
  const [showContactForm, setShowContactForm] = useState(true);
  const [selectedAiModel, setSelectedAiModel] = useState('');

  const getBotIDFromURL = () => {
    const urlParts = window.location.href.split('/');
    const botID = urlParts[urlParts.length - 1];
    return botID;
  };

  const botID = getBotIDFromURL();

  const fetchBotDetails = async () => {
    try {
      const data = await getBotDetails(botID);
      setBotName(data.botName);
      setBotId(data.botId);
      setCreatedDate(data.createdDate);
      setUpdatedBotName(data.botName);
      setBotStatus(data.botStatus);
      setIsActive(data.botStatus === 'Active');
      setChatWindowVariant(data.chatWindowVariant);
      setShowContactForm(data.showContactForm !== undefined ? data.showContactForm : true);

      const initialSelectedAiModel = await getSelectedAiModel(botID);
      setSelectedAiModel(initialSelectedAiModel);
    } catch (error) {
      console.error('Error fetching bot details:', error);
    }
  };

  useEffect(() => {
    fetchBotDetails();
  }, [botID]);

  const handleUpdateBotDetails = async () => {
    const updatedDetails = {
      botName: updatedBotName,
      botStatus: isActive ? 'Active' : 'InActive',
      chatWindowVariant,
      showContactForm,
      selectedAiModel,
    };

    try {
      await updateBotDetails(botID, updatedDetails);
      // await updateSelectedAiModel(botID, selectedAiModel);
      setBotName(updatedBotName);
      setIsSaveButtonDisabled(true);
      Swal.fire({
        icon: 'success',
        title: 'Data updated successfully!',
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (error) {
      console.error('Error updating bot details or saving selected AI model:', error);
    }
  };

  useEffect(() => {
    const checkIfSaveButtonShouldBeDisabled = async () => {
      const currentSelectedAiModel = await getSelectedAiModel(botID);
      setIsSaveButtonDisabled(
        updatedBotName === botName &&
        isActive === (botStatus === 'Active') &&
        chatWindowVariant === '' &&
        // TODO: Add check for showContactForm if needed for dirty state tracking, 
        // but for now simpler is fine or we'd need another state for initial value
        selectedAiModel === currentSelectedAiModel
      );
    };

    checkIfSaveButtonShouldBeDisabled();
  }, [updatedBotName, botName, isActive, botStatus, chatWindowVariant, selectedAiModel, botID, showContactForm]);

  const handleAiModelChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newAiModel = e.target.value;
    setSelectedAiModel(newAiModel);

    try {
      const currentSelectedAiModel = await getSelectedAiModel(botID);
      setIsSaveButtonDisabled(newAiModel === currentSelectedAiModel);
    } catch (error) {
      console.error('Error fetching the current selected AI model:', error);
    }
  };

  return (
    <div className='card mb-5 mb-xl-5 p-10'>
      <div className='row mt-5 mb-6'>
        <label className='col-lg-2 col-form-label required fw-bold fs-6'>Bot Name</label>
        <div className='col-lg-4 fv-row'>
          <input
            type='text'
            className='form-control form-control-lg form-control-solid'
            value={updatedBotName}
            placeholder='Bot Name'
            onChange={(e) => {
              setUpdatedBotName(e.target.value);
              setIsSaveButtonDisabled(e.target.value === '');
            }}
          />
        </div>
      </div>
      <div className='row mt-5 mb-6'>
        <label className='col-lg-2 col-form-label required fw-bold fs-6'>Bot Id</label>
        <div className='col-lg-4 fv-row'>
          <input
            type='text'
            className='form-control form-control-lg form-control-solid'
            defaultValue={botID}
            placeholder='Bot Id'
            disabled
          />
        </div>
      </div>
      <div className='row mt-5 mb-6'>
        <label className='col-lg-2 col-form-label required fw-bold fs-6'>Created date</label>
        <div className='col-lg-4 fv-row'>
          <input
            type='text'
            className='form-control form-control-lg form-control-solid'
            defaultValue={createdDate}
            placeholder='Created Date'
            disabled
          />
        </div>
      </div>
      <div className='row mt-5 mb-6 align-items-sm-center'>
        <label className='col-lg-2 col-form-label required fw-bold fs-6'>Bot Status</label>
        <div className='col-lg-4 fv-row'>
          <label className='d-flex align-items-sm-center px-5'>
            <Switch
              onChange={(checked) => {
                setIsActive(checked);
                setIsSaveButtonDisabled(false); // Enable save on change
              }}
              checked={isActive}
              id='botStatusSwitch'
            />
            <span className='ms-4 text-muted'>{isActive ? 'Active' : 'InActive'}</span>
          </label>
        </div>
      </div>
      <div className='row mt-5 mb-6 align-items-sm-center'>
        <label className='col-lg-2 col-form-label required fw-bold fs-6'>Show Contact Form</label>
        <div className='col-lg-4 fv-row'>
          <label className='d-flex align-items-sm-center px-5'>
            <Switch
              onChange={(checked) => {
                setShowContactForm(checked);
                setIsSaveButtonDisabled(false); // Enable save on change
              }}
              checked={showContactForm}
              id='showContactFormSwitch'
            />
            <span className='ms-4 text-muted'>{showContactForm ? 'Yes' : 'No'}</span>
          </label>
        </div>
      </div>
      <div className='row mt-5 mb-6'>
        <label className='col-lg-2 col-form-label required fw-bold fs-6'>Chat window variant</label>
        <div className='col-lg-4 fv-row'>
          <select
            className='form-select'
            aria-label='Select Variant'
            value={chatWindowVariant}
            onChange={(e) => setChatWindowVariant(e.target.value)}
          >
            <option value=''>Select Variant</option>
            <option value='full-width'>Full width</option>
            <option value='full-width-mobile'>Full width mobile</option>
            <option value='right-bottom-corner'>Right bottom corner</option>
          </select>
        </div>
      </div>
      {/* <div className='row mt-5 mb-6'>
        <label className='col-lg-2 col-form-label required fw-bold fs-6'>AI Model</label>
        <div className='col-lg-4 fv-row'>
          <select
            className='form-select'
            aria-label='Select AI Model'
            value={selectedAiModel}
            onChange={handleAiModelChange}
          >
            <option value=''>Select AI Model</option>
            <option value='Openai'>OpenAI</option>
            <option value='Gemini'>Gemini</option>
          </select>
        </div>
      </div> */}
      <div className='card-footer d-flex justify-content-end py-1 px-3'>
        <button
          type='submit'
          className='btn btn-primary mt-2'
          onClick={handleUpdateBotDetails}
          disabled={isSaveButtonDisabled}
        >
          Save Changes
        </button>
      </div>
    </div>
  );
};
